import { type ClientWebhook } from "@gen/app-query";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { ActionDialog } from "~/components/action_dialog";
import { CopyableTextInput } from "~/components/copyable_text_input";
import { ClearableSelectWithPlaceholder } from "~/components/select_with_placeholder";

type FormValues = Pick<ClientWebhook, "id">;

interface EncodedClientKeyDialogProps {
  open: boolean;
  onClose: () => void;
  webhooks: ClientWebhook[];
  apiKey?: string;
  clientSecret?: string;
  clientId: string;
}

export function EncodedClientKeyDialog({
  open,
  onClose,
  webhooks,
  apiKey,
  clientSecret,
  clientId,
}: EncodedClientKeyDialogProps) {
  const [encodedKey, setEncodedKey] = useState<string>("");

  const { control, watch, reset } = useForm<FormValues>({
    defaultValues: { id: "" },
  });

  const selectedWebhookId = watch("id");

  const selectedWebhook = useMemo(
    () => webhooks.find((w) => w.id === selectedWebhookId),
    [selectedWebhookId, webhooks],
  );

  useEffect(() => {
    if (!open) {
      reset();
      setEncodedKey("");
    }
  }, [open, reset]);

  const canGenerate =
    !!selectedWebhook && !!apiKey && !!clientSecret && !!clientId;

  const handleGenerate = () => {
    if (!selectedWebhook || !apiKey || !clientSecret) {
      return;
    }

    const payload = {
      clientID: clientId,
      Username: selectedWebhook.username,
      Password: selectedWebhook.password,
      clientSecret: clientSecret,
      apiKey: apiKey,
    } as const;

    const base64 = btoa(JSON.stringify(payload));
    setEncodedKey(base64);
  };

  return (
    <>
      {open && (
        <ActionDialog
          heading="Plugin Key"
          message="This key is relevant only to WooCommerce or Shopify sellers who use the Trustap Payments plugin. Please input it in the plugin settings."
          confirmButtonMessage="View Plugin Key"
          cancelButtonMessage="Cancel"
          isDisabled={!canGenerate}
          confirm={handleGenerate}
          cancel={onClose}
          confirmButtonDataTestId="view-plugin-key-confirm-button"
          cancelButtonDataTestId="plugin-key-cancel-button"
        >
          {webhooks.length === 0 ? (
            <Typography variant="body2">
              No webhooks available – create one first.
            </Typography>
          ) : (
            <Stack spacing={5}>
              <FormControl fullWidth>
                <InputLabel id="webhook-select-label">Webhook</InputLabel>
                <Controller
                  control={control}
                  name="id"
                  render={({ field }) => (
                    <ClearableSelectWithPlaceholder
                      labelId="webhook-select-label"
                      label="Webhook"
                      placeholder="Choose Webhook"
                      {...field}
                      data-testid="webhook-select"
                    >
                      {webhooks.map((wh) => (
                        <MenuItem
                          key={wh.id}
                          value={wh.id}
                          data-testid={`webhook-option-${wh.id}`}
                        >
                          {wh.url}
                        </MenuItem>
                      ))}
                    </ClearableSelectWithPlaceholder>
                  )}
                />
              </FormControl>

              {encodedKey && (
                <CopyableTextInput
                  fullWidth
                  value={encodedKey}
                  disabled
                  data-testid="encoded-key-text"
                />
              )}
            </Stack>
          )}
        </ActionDialog>
      )}
    </>
  );
}
